import React, {useState, useRef, useEffect} from 'react';
import '../../styles/Dashboard.css'
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Card from "../../components/Card";

export default function Announce({ dark, setDark, sidebarOpen, setSidebarOpen }) {
    const toggleTheme = () => setDark(!dark);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [sent, setSent] = useState(false);

    // New fields for payload
    const [channelId, setChannelId] = useState('1176976194655232010');
    const [embedTitle, setEmbedTitle] = useState('');
    const [embedDescription, setEmbedDescription] = useState('');
    const [embedColor, setEmbedColor] = useState('#ff0000');
    const [colorOpen, setColorOpen] = useState(false);
    const colorRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!colorRef.current) return;
            if (!colorRef.current.contains(e.target)) setColorOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);
    const [embedFields, setEmbedFields] = useState([
        { name: '', value: '', inline: true }
    ]);

    const handleSend = (e) => {
        if (e && e.preventDefault) e.preventDefault();

        const embed = {
            title: embedTitle || undefined,
            description: embedDescription || undefined,
            color: (() => {
                if (!embedColor) return undefined;
                const v = String(embedColor).trim();
                if (v.startsWith('#')) return parseInt(v.slice(1), 16);
                const n = Number(v);
                return Number.isNaN(n) ? undefined : n;
            })(),
            fields: embedFields && embedFields.length ? embedFields.map(f => ({ name: f.name, value: f.value, inline: Boolean(f.inline) })) : undefined
        };

        const payload = {
            channelId: channelId || undefined,
            content: message || undefined,
            embed: (embed.title || embed.description || (embed.fields && embed.fields.length)) ? embed : undefined
        };

        // POST directly to public API endpoint as requested
        (async () => {
            try {
                const res = await fetch('https://api.industriegaming.fr/api/bot/announce', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!res.ok) {
                    // try to read JSON error body
                    let errBody = null;
                    try { errBody = await res.json(); } catch(e) { errBody = await res.text().catch(() => null); }
                    console.error('Announce send failed', res.status, errBody);
                    alert('Échec de l envoi : ' + (errBody && errBody.message ? errBody.message : res.status));
                    return;
                }

                // success
                let respBody = null;
                try { respBody = await res.json(); } catch(e) { respBody = await res.text().catch(() => null); }
                console.log('Announce sent OK', respBody);
                setSent(true);
                setTimeout(() => setSent(false), 3000);
            } catch (err) {
                console.error('Network error sending announce', err);
                // Common cause: CORS or network unreachable
                alert('Erreur réseau lors de l envoi — vérifie la console. Si l erreur est "Failed to fetch" cela peut être un problème CORS ou un blocage réseau.\n' + String(err));
            }
        })();
    };

    const addEmbedField = () => setEmbedFields(prev => [...prev, { name: '', value: '', inline: false }]);
    const removeEmbedField = (idx) => setEmbedFields(prev => prev.filter((_, i) => i !== idx));
    const updateEmbedField = (idx, key, val) => setEmbedFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));

    // channels for select
    const [channels, setChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [channelsError, setChannelsError] = useState(null);

    const fetchChannels = async () => {
        try {
            setChannelsLoading(true);
            setChannelsError(null);
            // Use absolute backend URL in development to avoid dev-server serving index.html
            // Use the public API endpoint directly
            const res = await fetch('https://api.industriegaming.fr/api/bot/guildChannels?guildId=1069541751343304764');
            if (!res.ok) throw new Error(`Status ${res.status}`);
            const body = await res.json();
            // body.ok true and body.data.channels expected
            const ch = (body && body.data && body.data.channels) ? body.data.channels : [];
            const textChannels = ch.filter(c => String(c.type).toLowerCase() === 'guildtext');
            setChannels(textChannels);
            // if no channel selected yet, pick the first available
            if ((!channelId || channelId === '') && textChannels.length) {
                setChannelId(textChannels[0].id);
            }
        } catch (err) {
            console.error('Error loading channels', err);
            setChannelsError(err.message || String(err));
        } finally {
            setChannelsLoading(false);
        }
    };

    useEffect(() => {
        fetchChannels();
    }, []);

    // normalize color input to a #rrggbb string for preview
    const colorToHex = (v) => {
        if (!v) return '#2f3136';
        try {
            const s = String(v).trim();
            if (s.startsWith('#')) {
                // ensure 7-char
                if (s.length === 4) {
                    // short hex like #f00 -> #ff0000
                    return '#' + s[1].repeat(2) + s[2].repeat(2) + s[3].repeat(2);
                }
                return s;
            }
            const n = Number(s);
            if (!Number.isNaN(n)) {
                let h = n.toString(16);
                if (h.length < 6) h = h.padStart(6, '0');
                return '#' + h;
            }
        } catch (e) {}
        return '#2f3136';
    };

    return (
        <div className={`dashboard ${dark ? "dark" : ""}`}>
            <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="main">
                <Topbar onToggleSidebar={() => setSidebarOpen(true)} onToggleTheme={toggleTheme} dark={dark} />

                <div className="content">

                    {/* Header similar to Torrents page (title + actions) */}
                    <div className="users-header">
                        <div className="users-header-logo">
                            <h1>Annonce</h1>
                            <p style={{ marginTop: 6, opacity: 0.8 }}>Créer et envoyer une annonce via le bot ou vers un canal sélectionné.</p>
                        </div>

                        <div className="users-header-logo">
                            <div className="results-count">
                                {sent ? 'Dernière annonce envoyée' : 'Prête à envoyer'}
                            </div>
                            <button className="add-user-btn" onClick={handleSend}>
                                Envoyer
                            </button>
                        </div>
                    </div>

                    <div className="card" style={{ marginBottom: 16 }}>
                        <div className="card-header">
                            <span className="card-title">Aperçu</span>
                        </div>

                        <div style={{ padding: 12 }}>
                            {message ? (
                                <div className="announce-raw-message" style={{ marginBottom: 12 }}>{message}</div>
                            ) : null}

                            {/* <div style={{ marginBottom: 8 }}><strong>Embed</strong></div> */}
                            {((embedTitle && embedTitle.trim()) || (embedDescription && embedDescription.trim()) || (embedFields && embedFields.some(f => f.name || f.value))) ? (
                                <div className="announce-embed">
                                    <div className="announce-embed-colorbar" style={{ background: colorToHex(embedColor) }} />
                                    <div className="announce-embed-body">
                                        {embedTitle ? <div className="announce-embed-title">{embedTitle}</div> : null}
                                        {embedDescription ? <div className="announce-embed-description">{embedDescription}</div> : null}

                                        {embedFields && embedFields.length ? (
                                            <div className="announce-embed-fields">
                                                {embedFields.filter(f => f.name || f.value).map((f, i) => (
                                                    <div key={i} className="announce-embed-field" style={{ minWidth: f.inline ? 120 : '100%', flex: f.inline ? '1 1 30%' : '1 1 100%' }}>
                                                        <div className="announce-embed-field-name">{f.name || '\u00A0'}</div>
                                                        <div className="announce-embed-field-value">{f.value || '\u00A0'}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: 8, color: '#94a3b8' }}>Aucun embed</div>
                            )}
                        </div>

                    </div>

                    <div className="card">
                        <div className="card-header">
                            <span className="card-title">Créer une annonce</span>
                        </div>

                        <form style={{ display: 'flex', flexDirection: 'column', gap: 12 }} onSubmit={handleSend}>
                            {/* <label>
                                Titre
                                <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} />
                            </label> */}

                            <label>
                                Message
                                <textarea className="form-textarea" value={message} onChange={(e) => setMessage(e.target.value)} />
                            </label>

                            <label>
                                Channel
                                {channelsLoading ? (
                                    <div style={{ padding: 8 }}>Chargement des salons...</div>
                                ) : channelsError ? (
                                    <div style={{ color: '#ef4444' }}>Erreur: {channelsError}</div>
                                ) : (
                                    <select className="form-input" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                                        {channels.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                )}
                                <div style={{ marginTop: 8 }}>
                                    <button type="button" className="action-btn" onClick={fetchChannels}>Rafraîchir</button>
                                </div>
                            </label>

                            <fieldset style={{ border: '1px solid #e3e7f0', padding: 12, borderRadius: 8 }}>
                                <legend style={{ padding: '0 8px' }}>Embed</legend>

                                <label>
                                    Titre de l'embed
                                    <input className="form-input" value={embedTitle} onChange={(e) => setEmbedTitle(e.target.value)} />
                                </label>

                                <label>
                                    Description de l'embed
                                    <textarea className="form-textarea" value={embedDescription} onChange={(e) => setEmbedDescription(e.target.value)} />
                                </label>

                                <label>
                                    Couleur
                                    <div className="color-picker" ref={colorRef}>
                                        <button type="button" className="color-swatch" onClick={() => setColorOpen(v => !v)} style={{ background: embedColor }} aria-label="Choisir couleur" />
                                        <input className="form-input" value={embedColor} onChange={(e) => setEmbedColor(e.target.value)} />

                                        {colorOpen && (
                                            <div className="color-popover">
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <input
                                                        type="color"
                                                        className="native-color-input"
                                                        value={(embedColor && String(embedColor).startsWith('#')) ? embedColor : (() => {
                                                            try {
                                                                const n = Number(embedColor);
                                                                if (!Number.isNaN(n)) {
                                                                    let h = n.toString(16);
                                                                    if (h.length < 6) h = h.padStart(6, '0');
                                                                    return '#' + h;
                                                                }
                                                            } catch {}
                                                            return '#ff0000';
                                                        })()}
                                                        onChange={(e) => setEmbedColor(e.target.value)}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <input className="form-input" value={embedColor} onChange={(e) => setEmbedColor(e.target.value)} />
                                                    </div>
                                                </div>

                                                <div className="color-presets">
                                                    {['#ff4757', '#00b894', '#0984e3', '#fdcb6e', '#6c5ce7', '#d63031', '#e84393', '#00cec9'].map((c) => (
                                                        <div key={c} className="preset-swatch" style={{ background: c }} onClick={() => setEmbedColor(c)} />
                                                    ))}
                                                </div>

                                                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                                                    <button type="button" className="action-btn" onClick={() => setColorOpen(false)}>OK</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </label>

                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                        <strong>Champs</strong>
                                        <button type="button" className="action-btn" onClick={addEmbedField}>Ajouter un champ</button>
                                    </div>

                                    {embedFields.map((f, idx) => (
                                        <div key={idx} className="embed-field-row">
                                            <input className="form-input" placeholder="Nom" value={f.name} onChange={(e) => updateEmbedField(idx, 'name', e.target.value)} />
                                            <input className="form-input" placeholder="Valeur" value={f.value} onChange={(e) => updateEmbedField(idx, 'value', e.target.value)} />
                                            <div className="embed-field-actions">
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <input type="checkbox" checked={!!f.inline} onChange={(e) => updateEmbedField(idx, 'inline', e.target.checked)} /> Inline
                                                </label>
                                                <button type="button" className="action-btn" onClick={() => removeEmbedField(idx)}>Suppr</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </fieldset>

                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="action-btn" type="submit">Envoyer</button>
                                {sent ? <div style={{ alignSelf: 'center', color: '#10b981' }}>Envoyé</div> : null}
                            </div>
                        </form>
                    </div>

                    
                </div>
            </div>
        </div>
    );
}
